import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import DiamondIcon from '@mui/icons-material/Diamond';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PremiumAccessService from '../../services/PremiumAccessService';
import authService from '../../services/AuthService';
import splinterlandsService from '../../services/SplinterlandsService';

const PurchasePremiumModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('');
  
  const RECIPIENT_ACCOUNT = 'elmo.market';
  const AMOUNT = PremiumAccessService.SUBSCRIPTION_PRICE;
  const MEMO = 'PREMIUM_SUBSCRIPTION';
  
  const currentUser = authService.getCurrentUser();
  
  const handleClose = () => {
    setTxHash('');
    setError(null);
    setSubscriptionId(null);
    setSuccess(false);
    onClose();
  };
  
  const handlePurchase = async () => {
    if (!currentUser || !currentUser.username) {
      setError('Please login first');
      return;
    }

    // Check if Hive Keychain is installed
    if (!window.hive_keychain) {
      setError('Hive Keychain extension not found. Please install it first.');
      return;
    }

    // Check DEC balance
    const balances = splinterlandsService.getBalances();
    const decBalance = balances.dec || 0;
    
    if (decBalance < AMOUNT) {
      setError(`Insufficient DEC balance. You have ${decBalance.toFixed(3)} DEC but need ${AMOUNT} DEC.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Create pending subscription first
      const subscriptionResult = await PremiumAccessService.createSubscription(null);
      
      if (!subscriptionResult.success) {
        setError(subscriptionResult.error || 'Failed to create subscription');
        setLoading(false);
        return;
      }
      
      const pendingSubscriptionId = subscriptionResult.subscription.id;
      
      // Step 2: Request DEC payment via Hive Keychain (Splinterlands Token Transfer)
      const transferData = {
        token: 'DEC',
        to: RECIPIENT_ACCOUNT,
        qty: AMOUNT,
        memo: `${MEMO}:${currentUser.username}`,
        app: 'elmo/1.0.0',
        n: Math.random().toString(36).substring(2, 10)
      };
      
      window.hive_keychain.requestCustomJson(
        currentUser.username,
        'sm_token_transfer',
        'Active',
        JSON.stringify(transferData),
        'Premium Subscription Payment',
        async (response) => {
          if (response.success) {
            setTxHash(response.result.id);
            setProcessingMessage('Payment successful! Verifying on blockchain...');
            
            // Wait for blockchain confirmation and retry verification
            const maxRetries = 5;
            const retryDelay = 3000; // 3 seconds between retries
            
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                setProcessingMessage(`Verifying payment... (Attempt ${attempt}/${maxRetries})`);
                
                // Wait before attempting verification
                if (attempt > 1) {
                  await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
                
                const confirmResult = await PremiumAccessService.confirmPayment(
                  pendingSubscriptionId,
                  response.result.id
                );
                
                if (confirmResult.success) {
                  setSubscriptionId(confirmResult.subscription.id);
                  setSuccess(true);
                  setProcessingMessage('');
                  
                  // Clear premium access cache to force refresh
                  if (window.premiumAccessCache) {
                    window.premiumAccessCache = {};
                  }
                  
                  // Trigger a full page reload after 2 seconds to refresh all premium content
                  setTimeout(() => {
                    window.location.reload();
                  }, 2000);
                  
                  if (onSuccess) {
                    onSuccess(confirmResult);
                  }
                  setLoading(false);
                  return; // Success - exit retry loop
                } else {
                  console.log(`Verification attempt ${attempt} failed:`, confirmResult.error);
                  
                  // Only show error on last attempt
                  if (attempt === maxRetries) {
                    setError('Payment verification failed. Please contact support with transaction: ' + response.result.id);
                  }
                }
              } catch (err) {
                console.error(`Error on attempt ${attempt}:`, err);
                
                // Only show error on last attempt
                if (attempt === maxRetries) {
                  setError('Failed to verify payment. Please contact support with transaction: ' + response.result.id);
                }
              }
            }
            
            setLoading(false);
            setProcessingMessage('');
          } else {
            setError(response.message || 'Payment cancelled or failed');
          }
          setLoading(false);
        }
      );
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Error processing payment:', err);
      setLoading(false);
    }
  };
  
  if (!currentUser) {
    return null;
  }
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        backgroundColor: colors.primary[400],
        borderBottom: `1px solid ${colors.grey[700]}`
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <DiamondIcon sx={{ color: colors.greenAccent[400] }} />
          Purchase Premium Access
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ backgroundColor: colors.primary[400], pt: 3 }}>
        {!success ? (
          <Box>
            {processingMessage && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {processingMessage}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
            {error && !processingMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
                {error}
              </Alert>
            )}
            
            <Typography variant="h6" gutterBottom>
              Premium Subscription
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Get full access to all premium charts and features for 30 days!
            </Alert>
            
            <Box sx={{ 
              backgroundColor: colors.primary[500], 
              p: 3, 
              borderRadius: 1,
              border: `2px solid ${colors.greenAccent[400]}`,
              textAlign: 'center',
              mb: 3
            }}>
              <Typography variant="h4" fontWeight="bold" color={colors.greenAccent[400]}>
                {AMOUNT} DEC
              </Typography>
              <Typography variant="body2" color={colors.grey[300]}>
                for 30 days access
              </Typography>
            </Box>

            <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
              Payment will be processed via Hive Keychain. Make sure you have the extension installed.
            </Typography>
            
            {!window.hive_keychain && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Hive Keychain not detected. Please install the{' '}
                <Link href="https://hive-keychain.com/" target="_blank" rel="noopener">
                  Hive Keychain extension
                </Link>
                {' '}first.
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box textAlign="center">
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                color: colors.greenAccent[400],
                mb: 2
              }} 
            />
            
            <Typography variant="h5" gutterBottom>
              Premium Activated!
            </Typography>
            
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              Your premium subscription is now active for 30 days!
            </Alert>
            
            {txHash && (
              <Typography variant="body2" color={colors.grey[300]} gutterBottom>
                Transaction: {txHash.substring(0, 20)}...
              </Typography>
            )}
            
            <Typography variant="body2" color={colors.grey[400]} sx={{ mt: 2 }}>
              You now have full access to all premium charts and features.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        backgroundColor: colors.primary[400],
        borderTop: `1px solid ${colors.grey[700]}`,
        p: 2
      }}>
        {success ? (
          <Button 
            onClick={handleClose}
            variant="contained"
            color="primary"
            fullWidth
          >
            Start Using Premium Features
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handlePurchase}
              variant="contained"
              color="secondary"
              disabled={loading || !window.hive_keychain}
              startIcon={loading ? <CircularProgress size={20} /> : <DiamondIcon />}
            >
              {loading ? 'Processing...' : `Pay ${AMOUNT} DEC`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePremiumModal;
